import React, { useState, useMemo, useEffect } from 'react';
import MathText from '../../VueQuestion/MathText';
import { attendusDeRubrique } from '../../../utils/rubriqueAttendus';
import { X, BookOpen, Target, ChevronDown, Check, Sparkles, Search } from 'lucide-react';

// Composant pour une option de savoir/attendu
const SavoirOption = ({ label, onChange, checked, type }) => {
  const renderTextWithLineBreaks = (text) => {
    if (!text) return '';
    return text.split('<br>').map((line, index, array) => (
      <React.Fragment key={index}>
        <MathText>{line}</MathText>
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const isSavoir = type === 'savoir';

  return (
    <div
      onClick={onChange}
      className={`
        flex items-center gap-4 xl:gap-2 2xl:gap-3 3xl:gap-4 p-4 xl:p-2 2xl:p-3 3xl:p-4 border-2 rounded-xl cursor-pointer
        transition-all duration-300 relative overflow-hidden group
        ${checked
          ? isSavoir
            ? 'border-blue-400 bg-gradient-to-r from-blue-50 to-sky-50 shadow-md'
            : 'border-emerald-400 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md'
          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30'
        }
      `}
    >
      {/* Barre latérale animée */}
      <div className={`
        absolute left-0 top-0 bottom-0 w-1 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300
        ${isSavoir ? 'bg-gradient-to-b from-blue-500 to-sky-500' : 'bg-gradient-to-b from-emerald-500 to-teal-500'}
      `} />

      {/* Icône type */}
      <div className={`
        w-8 h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 rounded-lg flex items-center justify-center flex-shrink-0
        ${checked
          ? isSavoir ? 'bg-blue-500 text-white' : 'bg-emerald-500 text-white'
          : 'bg-gray-100 text-gray-400'
        }
      `}>
        {isSavoir ? <BookOpen className="w-4 h-4" /> : <Target className="w-4 h-4" />}
      </div>

      {/* Label */}
      <div className="flex-1">
        <span className={`font-medium leading-relaxed text-sm xl:text-xs 2xl:text-xs 3xl:text-sm ${
          checked
            ? isSavoir ? 'text-blue-700' : 'text-emerald-700'
            : 'text-gray-700'
        }`}>
          {renderTextWithLineBreaks(label)}
        </span>
      </div>

      {/* Checkbox */}
      <div className={`
        w-6 h-6 xl:w-5 xl:h-5 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 border-2 rounded-lg flex items-center justify-center transition-all duration-300
        ${checked
          ? isSavoir
            ? 'bg-blue-500 border-blue-500 text-white'
            : 'bg-emerald-500 border-emerald-500 text-white'
          : 'border-gray-300 bg-white'
        }
      `}>
        {checked && <Check className="w-4 h-4" />}
      </div>
    </div>
  );
};

function AddSavoirToSequence({ isOpen, onClose, sequence, theme, onSave, isLoading = false, dejaAbordes = null }) {
  const [selectedSavoirs, setSelectedSavoirs] = useState([]);
  const [selectedAttendus, setSelectedAttendus] = useState([]);
  const [activeTab, setActiveTab] = useState('savoir');
  const [expandedDescripteur, setExpandedDescripteur] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialiser avec les savoirs/attendus déjà présents dans la séquence
  useEffect(() => {
    if (sequence) {
      setSelectedSavoirs(sequence.savoir_faires_details || []);
      setSelectedAttendus(sequence.attendus_details || []);
    }
  }, [sequence]);

  // Récupérer les descripteurs du thème, filtrés par la RUBRIQUE de la
  // cible (décision 2026-08-18, étendue 2026-08-25) : une SÉQUENCE ne
  // porte que des OBJECTIFS ; un RITUEL (type AUTOMATISME) se nourrit des
  // attendus de la rubrique AUTOMATISME.
  const rubriqueCible = sequence?.type === 'AUTOMATISME' ? 'automatisme' : 'objectif';
  const descripteursSource = useMemo(() => {
    return (theme?.descripteur || []).map((desc) => ({
      ...desc,
      attendus: attendusDeRubrique(desc.attendus, rubriqueCible),
    }));
  }, [theme, rubriqueCible]);

  // Filtre « uniquement les attendus restants » (demande prof 2026-08-25),
  // ACTIF par défaut : ne montrer que ce qui n'est encore abordé nulle
  // part dans la progression — le prof voit ce qui reste à placer. Un
  // attendu déjà coché dans la cible n'est JAMAIS masqué, et
  // l'interrupteur réaffiche tout (le retravail spiralaire reste
  // possible). Rendu / recherche / compteurs suivent ce même filtre.
  const [seulementRestants, setSeulementRestants] = useState(true);
  const selectedAttenduIds = useMemo(
    () => new Set(selectedAttendus.map((a) => a.id)),
    [selectedAttendus]
  );
  const descripteurs = useMemo(() => {
    if (!dejaAbordes || !seulementRestants) return descripteursSource;
    return descripteursSource.map((desc) => ({
      ...desc,
      attendus: (desc.attendus || []).filter(
        (a) => !dejaAbordes.has(a.id) || selectedAttenduIds.has(a.id)
      ),
    }));
  }, [descripteursSource, dejaAbordes, seulementRestants, selectedAttenduIds]);
  const nbMasques = useMemo(() => {
    const compte = (liste) => liste.reduce((s, d) => s + (d.attendus || []).length, 0);
    return compte(descripteursSource) - compte(descripteurs);
  }, [descripteursSource, descripteurs]);

  // Vérifier si des savoirs_faires existent
  const hasSavoirsFaires = useMemo(() => {
    return descripteurs.some(desc => desc.savoirs_faires && desc.savoirs_faires.length > 0);
  }, [descripteurs]);

  // Auto-switch vers attendus si pas de savoirs
  useEffect(() => {
    if (!hasSavoirsFaires) {
      setActiveTab('attendu');
    }
  }, [hasSavoirsFaires]);

  // Résultats de recherche : liste plate de tous les savoirs/attendus correspondants
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const results = { savoirs: [], attendus: [] };

    descripteurs.forEach(desc => {
      // Recherche dans les savoirs-faires
      (desc.savoirs_faires || []).forEach(s => {
        if ((s.nom || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.savoirs.push({ ...s, descripteurNom: desc.nom });
        }
      });

      // Recherche dans les attendus
      (desc.attendus || []).forEach(a => {
        if ((a.description || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.attendus.push({ ...a, descripteurNom: desc.nom });
        }
      });
    });

    return results;
  }, [descripteurs, searchQuery]);

  const handleSavoirChange = (savoir) => {
    setSelectedSavoirs(prev => {
      const exists = prev.some(s => s.id === savoir.id);
      if (exists) {
        return prev.filter(s => s.id !== savoir.id);
      }
      return [...prev, savoir];
    });
  };

  const handleAttenduChange = (attendu) => {
    setSelectedAttendus(prev => {
      const exists = prev.some(a => a.id === attendu.id);
      if (exists) {
        return prev.filter(a => a.id !== attendu.id);
      }
      return [...prev, attendu];
    });
  };

  const handleSave = () => {
    onSave({
      savoir_faires_details: selectedSavoirs,
      attendus_details: selectedAttendus
    });
    setSearchQuery('');
    onClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-blue-500/20 w-full max-w-3xl max-h-[90vh] overflow-hidden border border-white/50 flex flex-col">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-base font-semibold text-slate-800">Enregistrement en cours...</p>
            <p className="text-sm text-slate-500 mt-1">Veuillez patienter</p>
          </div>
        )}

        {/* Header avec glassmorphism */}
        <div className="relative overflow-hidden flex-shrink-0">
          {/* Glow effect */}
          <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 rounded-3xl blur-2xl opacity-30 animate-pulse" />

          <div className="relative overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-sky-500 to-cyan-400" />

            {/* Animated liquid blobs */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 150" preserveAspectRatio="none">
              <defs>
                <linearGradient id="savoir-blob1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#c4b5fd" stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="savoir-blob2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.1" />
                </linearGradient>
              </defs>

              {/* First blob */}
              <path fill="url(#savoir-blob1)">
                <animate attributeName="d" dur="8s" repeatCount="indefinite"
                  values="M0,80 Q200,40 400,60 Q600,90 800,50 L800,150 L0,150 Z;
                          M0,50 Q200,80 400,40 Q600,60 800,90 L800,150 L0,150 Z;
                          M0,80 Q200,40 400,60 Q600,90 800,50 L800,150 L0,150 Z" />
              </path>

              {/* Second blob */}
              <path fill="url(#savoir-blob2)">
                <animate attributeName="d" dur="6s" repeatCount="indefinite"
                  values="M0,100 Q300,70 500,85 Q700,110 800,75 L800,150 L0,150 Z;
                          M0,75 Q300,100 500,70 Q700,80 800,110 L800,150 L0,150 Z;
                          M0,100 Q300,70 500,85 Q700,110 800,75 L800,150 L0,150 Z" />
              </path>

              {/* Floating particles */}
              <circle cx="120" cy="60" r="2" fill="white" opacity="0.4">
                <animate attributeName="cy" dur="3s" values="60;35;60" repeatCount="indefinite" />
              </circle>
              <circle cx="680" cy="80" r="2" fill="white" opacity="0.3">
                <animate attributeName="cy" dur="4s" values="80;50;80" repeatCount="indefinite" />
              </circle>
              <circle cx="400" cy="50" r="2.5" fill="white" opacity="0.35">
                <animate attributeName="cy" dur="3.5s" values="50;30;50" repeatCount="indefinite" />
              </circle>
            </svg>

            {/* Content layer - Header */}
            <div className="relative px-6 xl:px-4 2xl:px-5 3xl:px-6 py-5 xl:py-3 2xl:py-4 3xl:py-5 flex items-center justify-between">
              <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-white/20 backdrop-blur rounded-xl border border-white/30 shadow-xl">
                  <Sparkles className="w-6 h-6 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl xl:text-sm 2xl:text-base 3xl:text-xl font-bold text-white">
                    Ajouter des savoirs
                  </h2>
                  <p className="text-white/80 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm">
                    Séquence : {sequence?.title}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 bg-orange-500/80 hover:bg-orange-600 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Tabs dans le header */}
            <div className="relative px-6 xl:px-4 2xl:px-5 3xl:px-6 pb-4 xl:pb-2 2xl:pb-3 3xl:pb-4">
              <div className="flex items-center justify-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                {hasSavoirsFaires && (
                  <button
                    onClick={() => setActiveTab('savoir')}
                    className={`flex items-center gap-2 px-5 xl:px-3 2xl:px-4 3xl:px-5 py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 rounded-full transition-all duration-300 ${
                      activeTab === 'savoir'
                        ? 'bg-white text-blue-600 shadow-lg scale-105'
                        : 'bg-white/20 text-white/80 hover:bg-white/30'
                    }`}
                  >
                    <BookOpen className="w-4 h-4" />
                    <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium">Savoir-faire</span>
                    {selectedSavoirs.length > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        activeTab === 'savoir' ? 'bg-blue-100 text-blue-600' : 'bg-white/30 text-white'
                      }`}>
                        {selectedSavoirs.length}
                      </span>
                    )}
                  </button>
                )}

                <button
                  onClick={() => setActiveTab('attendu')}
                  className={`flex items-center gap-2 px-5 xl:px-3 2xl:px-4 3xl:px-5 py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 rounded-full transition-all duration-300 ${
                    activeTab === 'attendu'
                      ? 'bg-white text-emerald-600 shadow-lg scale-105'
                      : 'bg-white/20 text-white/80 hover:bg-white/30'
                  }`}
                >
                  <Target className="w-4 h-4" />
                  <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium">Attendus</span>
                  {selectedAttendus.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeTab === 'attendu' ? 'bg-emerald-100 text-emerald-600' : 'bg-white/30 text-white'
                    }`}>
                      {selectedAttendus.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 xl:p-3 2xl:p-4 3xl:p-6 overflow-y-auto flex-1 min-h-0">
          {/* Filtre « attendus restants non enseignés ailleurs » */}
          {activeTab === 'attendu' && dejaAbordes ? (
            <div className="mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3 flex items-center flex-wrap gap-2">
              <button
                onClick={() => setSeulementRestants((v) => !v)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all ${
                  seulementRestants
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border-2 ${
                  seulementRestants ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                }`}>
                  {seulementRestants ? <Check className="w-2.5 h-2.5 text-white" /> : null}
                </span>
                Uniquement les attendus restants
              </button>
              {seulementRestants && nbMasques > 0 ? (
                <span className="text-[11px] font-semibold text-gray-400">
                  {nbMasques} déjà abordé{nbMasques > 1 ? 's' : ''} masqué{nbMasques > 1 ? 's' : ''}
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Champ de recherche */}
          <div className="mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
            <div className="relative">
              <Search className="absolute left-4 xl:left-3 top-1/2 -translate-y-1/2 w-5 h-5 xl:w-4 xl:h-4 text-black" />
              <input
                type="text"
                placeholder={activeTab === 'savoir' ? "Rechercher un savoir-faire..." : "Rechercher un attendu..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 xl:pl-10 pr-4 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-2 border-gray-200 rounded-xl text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                  focus:border-blue-400 focus:ring-2 text-black focus:ring-blue-100 focus:outline-none
                  transition-all duration-300 bg-white/80"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-200 rounded-full
                    flex items-center justify-center hover:bg-gray-300 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>
          </div>

          {/* Affichage des résultats de recherche */}
          {searchResults ? (
            <div className="space-y-3">
              {/* Résultats savoirs-faires */}
              {activeTab === 'savoir' && (
                searchResults.savoirs.length > 0 ? (
                  <>
                    <p className="text-xs text-gray-500 mb-2">
                      {searchResults.savoirs.length} savoir-faire(s) trouvé(s)
                    </p>
                    {searchResults.savoirs.map((savoir) => (
                      <div key={savoir.id} className="relative">
                        <span className="absolute -top-2 left-3 px-2 py-0.5 bg-blue-100 text-blue-600 text-[10px] rounded-full z-10">
                          {savoir.descripteurNom}
                        </span>
                        <SavoirOption
                          label={savoir.nom}
                          onChange={() => handleSavoirChange(savoir)}
                          checked={selectedSavoirs.some(s => s.id === savoir.id)}
                          type="savoir"
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">Aucun savoir-faire trouvé pour "{searchQuery}"</p>
                  </div>
                )
              )}

              {/* Résultats attendus */}
              {activeTab === 'attendu' && (
                searchResults.attendus.length > 0 ? (
                  <>
                    <p className="text-xs text-gray-500 mb-2">
                      {searchResults.attendus.length} attendu(s) trouvé(s)
                    </p>
                    {searchResults.attendus.map((attendu) => (
                      <div key={attendu.id} className="relative">
                        <span className="absolute -top-2 left-3 px-2 py-0.5 bg-emerald-100 text-emerald-600 text-[10px] rounded-full z-10">
                          {attendu.descripteurNom}
                        </span>
                        <SavoirOption
                          label={attendu.description}
                          onChange={() => handleAttenduChange(attendu)}
                          checked={selectedAttendus.some(a => a.id === attendu.id)}
                          type="attendu"
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">Aucun attendu trouvé pour "{searchQuery}"</p>
                  </div>
                )
              )}
            </div>
          ) : (
            /* Liste des descripteurs (affichage par défaut sans recherche) */
            <div className="space-y-3">
              {descripteurs.map((desc) => (
                <div
                  key={desc.id}
                  className="border-2 border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-blue-300 hover:shadow-md"
                >
                  {/* Header du descripteur */}
                  <div
                    onClick={() => setExpandedDescripteur(expandedDescripteur === desc.id ? null : desc.id)}
                    className={`
                      flex items-center justify-between p-4 xl:p-2 2xl:p-3 3xl:p-4 cursor-pointer transition-all duration-300
                      ${expandedDescripteur === desc.id
                        ? 'bg-gradient-to-r from-blue-50 to-sky-50 border-b-2 border-blue-200'
                        : 'bg-gradient-to-r from-gray-50 to-slate-50 hover:from-blue-50 hover:to-sky-50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                      <div className={`
                        w-8 h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 rounded-lg flex items-center justify-center
                        ${expandedDescripteur === desc.id ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}
                      `}>
                        <BookOpen className="w-4 h-4" />
                      </div>
                      <h4 className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-bold text-gray-800">{desc.nom}</h4>
                    </div>
                    <ChevronDown className={`
                      w-5 h-5 text-blue-500 transform transition-transform duration-300
                      ${expandedDescripteur === desc.id ? 'rotate-180' : ''}
                    `} />
                  </div>

                  {/* Contenu collapsible */}
                  {expandedDescripteur === desc.id && (
                    <div className="p-4 xl:p-2 2xl:p-3 3xl:p-4 bg-white space-y-3 xl:space-y-2 2xl:space-y-2.5 3xl:space-y-3">
                      {activeTab === 'savoir' ? (
                        desc.savoirs_faires && desc.savoirs_faires.length > 0 ? (
                          desc.savoirs_faires.map((savoir) => (
                            <SavoirOption
                              key={savoir.id}
                              label={savoir.nom}
                              onChange={() => handleSavoirChange(savoir)}
                              checked={selectedSavoirs.some(s => s.id === savoir.id)}
                              type="savoir"
                            />
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic text-center py-4">
                            Aucun savoir-faire disponible pour ce descripteur
                          </p>
                        )
                      ) : (
                        desc.attendus && desc.attendus.length > 0 ? (
                          desc.attendus.map((attendu) => (
                            <SavoirOption
                              key={attendu.id}
                              label={attendu.description}
                              onChange={() => handleAttenduChange(attendu)}
                              checked={selectedAttendus.some(a => a.id === attendu.id)}
                              type="attendu"
                            />
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic text-center py-4">
                            Aucun attendu disponible pour ce descripteur
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}

              {descripteurs.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">Aucun descripteur disponible pour ce thème</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer avec résumé et boutons */}
        <div className="border-t border-gray-200 p-6 xl:p-3 2xl:p-4 3xl:p-6 bg-gray-50/80 flex-shrink-0">
          {/* Résumé des sélections */}
          {(selectedSavoirs.length > 0 || selectedAttendus.length > 0) && (
            <div className="mb-4 xl:mb-3 2xl:mb-3 3xl:mb-4 p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl text-white">
              <div className="flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                <Check className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-3.5 2xl:h-3.5 3xl:w-4 3xl:h-4" />
                <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-bold">
                  {selectedSavoirs.length + selectedAttendus.length} élément(s) sélectionné(s)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 max-h-20 xl:max-h-16 2xl:max-h-16 3xl:max-h-20 overflow-y-auto">
                {selectedSavoirs.map((s) => (
                  <span
                    key={s.id}
                    className="inline-flex items-center gap-1 px-2 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-1 bg-white/20 rounded-full text-xs xl:text-[10px] 2xl:text-[10px] 3xl:text-xs"
                  >
                    <BookOpen className="w-3 h-3" />
                    <span className="max-w-[150px] truncate">{s.nom}</span>
                    <button
                      onClick={() => handleSavoirChange(s)}
                      className="w-4 h-4 bg-white/30 rounded-full flex items-center justify-center hover:bg-white/50"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedAttendus.map((a) => (
                  <span
                    key={a.id}
                    className="inline-flex items-center gap-1 px-2 xl:px-1.5 2xl:px-1.5 3xl:px-2 py-1 bg-black/20 rounded-full text-xs xl:text-[10px] 2xl:text-[10px] 3xl:text-xs"
                  >
                    <Target className="w-3 h-3" />
                    <span className="max-w-[150px] truncate">{a.description}</span>
                    <button
                      onClick={() => handleAttenduChange(a)}
                      className="w-4 h-4 bg-white/30 rounded-full flex items-center justify-center hover:bg-white/50"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-between items-center">
            <button
              onClick={handleClose}
              className="px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 bg-orange-500 text-white rounded-xl font-medium xl:text-xs 2xl:text-sm 3xl:text-base hover:bg-orange-600 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={selectedSavoirs.length === 0 && selectedAttendus.length === 0}
              className={`
                px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl font-medium xl:text-xs 2xl:text-sm 3xl:text-base transition-all duration-300
                ${selectedSavoirs.length > 0 || selectedAttendus.length > 0
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg hover:shadow-xl hover:scale-105'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Enregistrer les modifications
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddSavoirToSequence;
